import { api } from "./client";
import { locale } from "../i18n";
import type { ReportFilters } from "./reports";
import type { UsdBrl } from "../composables/fx";

const base = (slug: string) => `/v1/c/${slug}`;
const clean = (q: object) =>
  Object.fromEntries(
    Object.entries(q).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string | number>;

export type CostBucket =
  "marketing" | "utility" | "authentication" | "service" | "other";
export type Buckets = Record<CostBucket, number>;

export interface CostReport {
  period: { from: string; to: string; timezone: string };
  currency: string | null;
  mixedCurrencies: boolean;
  meta: {
    totalMicros: number;
    confirmedMicros: number;
    charged: number;
    byCategory: Buckets;
  };
  ai: { usdMicros: number; brlMicros: number | null };
  /** Today's dollar, which converts the AI above. */
  fx: UsdBrl | null;
  conversations: number;
  successes: number;
  perConversationMicros: number | null;
  perSuccessMicros: number | null;
  daily: Array<{ day: string } & Buckets>;
  previous: { total: number; daily: number[] } | null;
  bySource: Array<{ kind: string; micros: number }>;
  byTeam: Array<{ id: string | null; name: string | null; micros: number }>;
  byChannel: Array<{ id: string; name: string | null; micros: number }>;
  byMember: Array<{ id: string; name: string | null; micros: number }>;
  templates: Array<{
    id: string;
    name: string;
    category: string;
    messages: number;
    micros: number;
  }>;
  topConversations: Array<{
    id: string;
    contactName: string | null;
    micros: number;
    aiUsdMicros: number;
  }>;
  forecast: {
    month: string;
    spentMicros: number;
    forecastMicros: number;
    day: number;
    daysInMonth: number;
  };
  projection: {
    serviceMessages: number;
    numbers: number;
    freePerNumber: number;
    chargeable: number;
    projectedMicros: number;
  } | null;
}

export interface MetaCheck {
  days: Array<{
    day: string;
    estimatedMicros: number;
    metaMicros: number | null;
    differenceMicros: number | null;
    pendingMessages: number;
    status: "estimated" | "partial" | "confirmed" | "no_cost";
  }>;
  quota: Array<{
    channelId: string;
    name: string | null;
    used: number;
    free: number;
    exhausted: boolean;
  }>;
  tiers: Array<{ country: string; category: string; tier: string }>;
  lastSyncAt: string | null;
}

export interface CostAlert {
  id: string;
  kind:
    | "budget_threshold"
    | "daily_spike"
    | "category_changed"
    | "forecast_over"
    | "campaign_costly";
  data: Record<string, unknown>;
  createdAt: string;
}

export interface CostTrends {
  months: Array<{ month: string; metaMicros: number; aiMicros: number }>;
  fx: UsdBrl | null;
  forecast: {
    month: string;
    forecastMicros: number;
    day: number;
    daysInMonth: number;
  };
  alerts: CostAlert[];
  savings: {
    messagesPerReply: number | null;
    extraMessages: number;
    extraMicros: number;
    adConversations: number;
  };
}

export type LimitAction = "notify" | "pause_campaigns" | "pause_paid";
export interface CostRules {
  limitAction: LimitAction;
  thresholds: number[];
  notifyRoles: Array<"owner" | "admin" | "manager">;
  email: boolean;
  spikeFactor: number;
}
export interface ApprovalRule {
  enabled: boolean;
  overMicros: number | null;
  overAudience: number | null;
}
export interface BudgetStatus {
  id: string;
  scopeKind: "company" | "team";
  scopeId: string | null;
  scopeName: string | null;
  amountMicros: number;
  currency: string;
  spentMicros: number;
  forecastMicros: number;
  percent: number;
}
export interface Governance {
  rules: CostRules;
  approval: ApprovalRule;
  frequency: { marketingDays: number };
  /** A campaign reaching a conversation already open: whose it stays. */
  openConversation: { mode: "keep" | "route_if_free" | "route" };
  budgets: BudgetStatus[];
  accounts: Array<{ id: string; name: string; templateInsights: boolean }>;
}

export interface ConversationCosts {
  currency: string | null;
  lines: Array<{
    bucket: CostBucket;
    messages: number;
    micros: number;
    free: number;
  }>;
  metaMicros: number;
  ai: { usdMicros: number; brlMicros: number | null };
  /** Today's dollar, which converts the AI above. */
  fx: UsdBrl | null;
  freeTierEndedAt: string | null;
  contact: { conversations: number; metaMicros: number; aiUsdMicros: number };
}

export const costsApi = {
  report: (slug: string, f: ReportFilters) =>
    api<CostReport>(`${base(slug)}/costs/report`, { query: clean(f) }),
  meta: (slug: string, f: ReportFilters) =>
    api<MetaCheck>(`${base(slug)}/costs/meta`, { query: clean(f) }),
  trends: (slug: string, timezone?: string) =>
    api<CostTrends>(`${base(slug)}/costs/trends`, {
      query: clean({ timezone }),
    }),
  dismissAlert: (slug: string, id: string) =>
    api(`${base(slug)}/costs/alerts/${id}/dismiss`, { method: "POST" }),
  governance: (slug: string) =>
    api<Governance>(`${base(slug)}/costs/governance`),
  saveGovernance: (
    slug: string,
    body: Partial<
      Pick<Governance, "rules" | "approval" | "frequency" | "openConversation">
    >,
  ) => api(`${base(slug)}/costs/governance`, { method: "PUT", body }),
  saveBudget: (
    slug: string,
    body: {
      scopeKind: "company" | "team";
      scopeId: string | null;
      amountMicros: number | null;
    },
  ) =>
    api<{ budgets: BudgetStatus[] }>(`${base(slug)}/costs/budgets`, {
      method: "PUT",
      body,
    }),
  sync: (slug: string) =>
    api<{
      accounts: number;
      points: number;
      reconciled: number;
      errors: string[];
    }>(`${base(slug)}/costs/sync`, { method: "POST" }),
  enableTemplateInsights: (slug: string, accountId: string) =>
    api(`${base(slug)}/costs/template-insights/${accountId}`, {
      method: "POST",
    }),
  conversation: (slug: string, conversationId: string) =>
    api<ConversationCosts>(
      `${base(slug)}/conversations/${conversationId}/costs`,
    ),
};

/** Money in the account's currency: R$ 1.234,56. */
export function money(
  micros: number | null | undefined,
  currency: string | null | undefined,
  precise = false,
): string {
  if (micros === null || micros === undefined) return "—";
  return new Intl.NumberFormat(locale.value, {
    style: "currency",
    currency: currency ?? "BRL",
    minimumFractionDigits: precise ? 2 : 2,
    maximumFractionDigits: precise ? 4 : 2,
  }).format(micros / 1e6);
}
